import { useCallback, useEffect, useState } from "react";

import type { OrgBillingSummary } from "@domains/application/types";
import { orgSessionManager } from "@domains/auth/services/orgSessionManager";
import { apiBaseUrls } from "@platform/constants/shared-constants";

export interface OrgStackStatus {
  alias: string;
  name: string;
  status: string;
}

export interface UseOrgRealtimeResult {
  stacks: OrgStackStatus[];
  billing: OrgBillingSummary | null;
  billingLoading: boolean;
  connected: boolean;
  refreshStacks: () => void;
  refreshBilling: () => void;
}

/**
 * useOrgRealtime — fetches org-level stack statuses and billing summary.
 *
 * Previously used Socket.IO (/org-events namespace) for real-time updates,
 * but no server-side gateway existed for that namespace. Now uses REST
 * polling (30s for stacks, 60s for billing).
 */
export function useOrgRealtime(
  orgAlias: string | undefined,
): UseOrgRealtimeResult {
  const [stacks, setStacks] = useState<OrgStackStatus[]>([]);
  const [billing, setBilling] = useState<OrgBillingSummary | null>(null);
  const [billingLoading, setBillingLoading] = useState(true);

  const fetchStacks = useCallback(async () => {
    if (!orgAlias) return;
    try {
      const res = await fetch(
        `${apiBaseUrls.BACKEND}/api/v1/applications?organizationAlias=${encodeURIComponent(orgAlias)}`,
        {
          headers: {
            Authorization: `Bearer ${orgSessionManager.getToken() ?? ""}`,
          },
        },
      );
      if (!res.ok) return;
      const data = await res.json();
      setStacks(
        (Array.isArray(data) ? data : []).map(
          (s: { alias: string; name: string; status: string }) => ({
            alias: s.alias,
            name: s.name,
            status: s.status,
          }),
        ),
      );
    } catch {
      // silent — initial fetch failure
    }
  }, [orgAlias]);

  const fetchBilling = useCallback(async () => {
    if (!orgAlias) return;
    setBillingLoading(true);
    try {
      const res = await fetch(
        `${apiBaseUrls.BACKEND}/api/v1/billing/org/${encodeURIComponent(orgAlias)}/summary`,
        {
          headers: {
            Authorization: `Bearer ${orgSessionManager.getToken() ?? ""}`,
          },
        },
      );
      if (!res.ok) return;
      const data = await res.json();
      setBilling(data as OrgBillingSummary);
    } catch {
      // silent — initial fetch failure
    } finally {
      setBillingLoading(false);
    }
  }, [orgAlias]);

  useEffect(() => {
    if (!orgAlias) return;
    void fetchStacks();
    void fetchBilling();

    // Poll stacks every 30s
    const stacksInterval = setInterval(() => void fetchStacks(), 30_000);
    // Poll billing every 60s
    const billingInterval = setInterval(() => void fetchBilling(), 60_000);

    return () => {
      clearInterval(stacksInterval);
      clearInterval(billingInterval);
    };
  }, [orgAlias, fetchStacks, fetchBilling]);

  return {
    stacks,
    billing,
    billingLoading,
    connected: true, // Always "connected" since we're using REST polling
    refreshStacks: fetchStacks,
    refreshBilling: fetchBilling,
  };
}
